import { AfterContentInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { VideoComponentBase } from '../../../../../models/video-component.base.interface';
import { VideoEntity, VideoLibraryEntity, VideoStreamingProviderEntity } from '../../../../../../../meta-data/app-meta-data.service';
import { VideoEvent } from '../../../../../models/video-event.model';
import { VideoEventType } from '../../../../../models/video-event-type.enum';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PlayerSdk } from '@api.video/player-sdk'
import { VideoPlayerBaseComponent } from '../../../base/video-player-base.component';
import { VideoUploadService } from '../../../../../services/video-upload.service';
import { DwSecurityUserService } from '@devwareapps/devware-cap';
import { VideoMessagingService } from '../../../../../services/video-messaging.service';
import { Subscription } from 'rxjs';
import { DwPubSubService } from '../../../../../../z-devware/pubsub/dw-pub-sub-service';

@Component({
  selector: 'app-video-player-apivideo',
  template: `
  <div class="video-player-shell">
    <div class="video-player-container" [ngStyle]="videoPlayerStyle">
      <div #avplayer id="avplayer" name="avplayer" class="video-player-surface">
      </div>
    </div>
  </div>
`
})
export class VideoPlayerApiVideoComponent extends VideoPlayerBaseComponent implements VideoComponentBase, AfterContentInit, OnDestroy {
  player: PlayerSdk;

  userSeeked: boolean = false;
  lastTimeData: any;
  seekFurthestTime: boolean;
  videoStartComplete: boolean;
  aspectRatio: number = 16 / 9;

  videoPlayerStyle: any = {
    width: '100%',
    // 'min-width': '400px',
    // 'min-height': '225px',
    'min-width': '350px',
    'min-height': '196px',
    'max-width': '1200px'
  }
  width: number;
  height: number;
  maxHeight: number;
  finalHeight: number;
  finalWidth: string;
  previousWidth: number;


  videoSetupComplete: boolean = false;
  videoToken: string;
  contentInitComplete: boolean;
  tokenLoaded: boolean;
  subscriptions: Subscription[] = [];


  constructor(dwSecurityUserService: DwSecurityUserService, videoMessagingService: VideoMessagingService, dwPubSubService: DwPubSubService,private elementRef: ElementRef, private videoUploadService: VideoUploadService) {
    super(dwSecurityUserService, videoMessagingService, dwPubSubService);
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  setupVideo(): void {
    if (!this.video || !this.videoStreamingProvider) {
      return;
    }
    
    super.setupVideo();
    this.setVideoPlayerStyle();

    if (this.video.IsPrivateVideo) {
      this.videoUploadService.getVideoToken(this.video.VideoStreamingProviderId, this.video.VideoKey)
        .subscribe(videoToken => {
          this.videoToken = videoToken;
          this.tokenLoaded = true;

          this.setupPlayer();
        });
    } else {
      this.tokenLoaded = true;

      this.setupPlayer();

    }
  }

  pauseVideo() {
    if (this.player) {
      this.player.pause();
    }
  }

  @HostListener('window:resize', ['$event'])
  public reset(event: any): void {
    // re-configure the grid style upon browser resize
    this.setVideoPlayerStyle();
  }

  setVideoPlayerStyle() {
    if (!this.elementRef?.nativeElement) {
      return;
    }
    this.width = this.elementRef.nativeElement?.firstChild?.clientWidth;
    const offsetTop: number = this.elementRef.nativeElement?.getBoundingClientRect()?.top || 0;

    if (!this.width) {

      setTimeout(() => {
        this.setVideoPlayerStyle();

      }, 50);
      return;
    }

    if (this.previousWidth === this.width) {
      return;
    }

    this.previousWidth = this.width;

    this.height = Math.round(this.width / this.aspectRatio) + 2;

    this.maxHeight = window.innerHeight - offsetTop - 100;

    this.finalHeight = this.height;
    this.finalWidth = '100%';

    if (this.finalHeight > this.maxHeight) {
      this.finalHeight = this.maxHeight;
      const adjustedWidth = Math.round(this.finalHeight * this.aspectRatio) - 2;

      this.finalWidth = `${adjustedWidth}px`;
    }

    // Height is deliberately NOT set here. The container's CSS owns
    // `aspect-ratio: 16 / 9` (see styles/features/lesson-view.scss), so
    // height is recomputed live from the rendered width by the browser.
    // Historically this was `height: \`${this.finalHeight}px\`` but the
    // upstream calculation bakes the height from an early (~100px)
    // clientWidth read and never recovers — CSS aspect-ratio avoids the
    // stale-width problem entirely.
    this.videoPlayerStyle = {
      'width': this.finalWidth,
      'min-width': '350px',
    };

    this.videoWidthChanged.emit(this.finalWidth);
  }


  ngAfterContentInit(): void {
    this.setVideoPlayerStyle();

    this.contentInitComplete = true;

    this.setupPlayer();
  }

  setupPlayer() {
    if (!this.contentInitComplete || !this.tokenLoaded) {
      return;
    }
    if (this.player) {
      return;
    }
    const videoKey = this.video?.VideoKey;

    if (this.videoToken) {
      this.player = new PlayerSdk("#avplayer", {
        id: videoKey,
        hideTitle: true,
        token: this.videoToken
      });
    } else {
      this.player = new PlayerSdk("#avplayer", {
        id: videoKey,
        hideTitle: true,
      });
    }

    this.videoSetupComplete = true;



    this.initVideoPlayer();
  }

  initComplete = false;

  initVideoPlayer() {

    if (this.initComplete) {

      return;
    }

    this.player.hideTitle();


    this.player.setTheme({
      backgroundText: 'black',
    });

    this.player.hideControls(['seekForward', 'playbackRate', 'download', 'more', 'chapters']);

    this.player.addEventListener("play", () => {

      this.notifyVideoStarted();
      
      this.onVideoEvent(VideoEventType.videoStarted);
    });

    this.player.addEventListener("pause", () => {
      this.onVideoEvent(VideoEventType.videoPaused);
    });

    this.player.addEventListener("ended", () => {
      this.onVideoEvent(VideoEventType.videoCompleted);
    });

    this.player.addEventListener("timeupdate", (eventData) => {
      const timeUpdateResult = this.updateCurrentTime(eventData.currentTime);

      if (timeUpdateResult.userSeeked) {
        this.player.setCurrentTime(this.furthestWatchedTime);
      }

      this.onVideoEvent(VideoEventType.videoProgress);
    });

    // this.player.setTheme({
    //   // link: 'red',
    //   // linkHover: 'aqua',
    //   // backgroundBottom: 'red',
    //   // trackBackground: 'green',
    //   backgroundText: 'black',

    //   // text: 'yellow',
    //   // backgroundTop: 'blue',
    //   // linkActive: 'purple'
    // });


    this.player.getDuration((duration) => {
      this.videoDuration = duration;
      this.initComplete = true;

      this.initCompleted.emit(true);

      this.setupVideoStartOptions();
    });
  }


  setupVideoStartOptions() {

    if (this.videoStreamingProvider.ContinueFromLastPosition) {

      if (this.furthestWatchedTime < this.videoDuration - 3) {
        this.player.setCurrentTime(this.furthestWatchedTime);
      }
    }

    if (this.videoStreamingProvider.AutoPlay) {
      this.player.setAutoplay(true);
    }
  }
}
